/**
 * Admin -> Edge Fleet -> Agentless collection (release 3.1 fleet wave 2a).
 *
 * Collection the controller runs ITSELF against a customer's systems - catalog
 * crawl, value sampling, residual scan, CMDB and identity pulls - is an agent
 * type like any other (`agentless_collector`): one fleet row per target, paused
 * per target from the device drawer, per type from the rollout controls. What
 * it does not share with the others is a place to say how hard it may lean on
 * those systems. This panel is that place:
 *
 *   - the current caps (runs at once, queries a second, statement timeout) and
 *     whether they are the defaults or who changed them, when and why;
 *   - how many runs are in flight now;
 *   - an edit that needs a reason (audited `edge.agentless.settings_updated`);
 *   - the most recent runs and how each ended.
 *
 * Backend (release 3.1 wave 2a):
 *   GET /v1/edge/agent-types/agentless_collector/settings -> { settings, running }
 *   PUT /v1/edge/agent-types/agentless_collector/settings  { maxConcurrentRuns, maxQueriesPerSecond, statementTimeoutMs, reason }
 *   GET /v1/edge/agentless/runs?limit=                     -> { runs }
 *
 * A controller before wave 2a answers 404: the panel says so rather than
 * showing an error, because nothing is wrong.
 */
import { useCallback, useEffect, useState } from 'react';
import { DatabaseZap, Loader2, Pencil, RefreshCw, Save, X } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AGENTLESS_LIMITS,
  ApiError,
  getAgentlessSettings,
  listAgentlessRuns,
  problemDetail,
  updateAgentlessSettings,
  type AgentlessRun,
  type AgentlessSettingsResponse,
} from '@/lib/api';

/** How many recent runs the panel shows. */
export const AGENTLESS_RECENT_RUNS = 20;

const KIND_LABEL: Record<string, string> = {
  catalog_crawl: 'Catalog crawl',
  value_sample: 'Value sampling',
  residual_scan: 'Residual scan',
  protected_copy: 'Protected copy',
  cmdb_pull: 'CMDB pull',
  identity_pull: 'Identity pull',
};

const OUTCOME_VARIANT: Record<string, 'success' | 'warning' | 'critical' | 'glass'> = {
  succeeded: 'success',
  failed: 'critical',
  paused: 'warning',
  refused: 'warning',
};

export function agentlessKindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind.replace(/_/g, ' ');
}

/** A run with no outcome and no finish time is still in flight. */
export function agentlessOutcomeLabel(run: Pick<AgentlessRun, 'outcome' | 'finishedAt'>): string {
  if (!run.outcome) return run.finishedAt ? 'ended, outcome not recorded' : 'running';
  return run.outcome;
}

export interface AgentlessForm {
  maxConcurrentRuns: string;
  maxQueriesPerSecond: string;
  /** Seconds in the form; the API takes milliseconds. */
  statementTimeoutSeconds: string;
  reason: string;
}

/**
 * The body for the PUT, or the first thing wrong with the form. Pure, so the
 * bounds are tested without a browser; they are the controller's own
 * (AGENTLESS_LIMITS), so the form never offers a value the server refuses.
 */
export function agentlessFormToInput(
  f: AgentlessForm,
):
  | { ok: true; input: { maxConcurrentRuns: number; maxQueriesPerSecond: number; statementTimeoutMs: number; reason: string } }
  | { ok: false; error: string } {
  const L = AGENTLESS_LIMITS;
  const runs = Number(f.maxConcurrentRuns);
  if (!f.maxConcurrentRuns.trim() || !Number.isInteger(runs) || runs < L.maxConcurrentRuns.min || runs > L.maxConcurrentRuns.max) {
    return { ok: false, error: `Runs at once must be a whole number from ${L.maxConcurrentRuns.min} to ${L.maxConcurrentRuns.max}.` };
  }
  const qps = Number(f.maxQueriesPerSecond);
  if (!f.maxQueriesPerSecond.trim() || !Number.isFinite(qps) || qps < L.maxQueriesPerSecond.min || qps > L.maxQueriesPerSecond.max) {
    return {
      ok: false,
      error: `Queries a second must be from ${L.maxQueriesPerSecond.min} to ${L.maxQueriesPerSecond.max.toLocaleString('en-IN')}.`,
    };
  }
  const seconds = Number(f.statementTimeoutSeconds);
  const ms = Math.round(seconds * 1000);
  if (!f.statementTimeoutSeconds.trim() || !Number.isFinite(seconds) || ms < L.statementTimeoutMs.min || ms > L.statementTimeoutMs.max) {
    return {
      ok: false,
      error: `Statement timeout must be from ${L.statementTimeoutMs.min / 1000} to ${L.statementTimeoutMs.max / 1000} seconds.`,
    };
  }
  const reason = f.reason.trim();
  if (reason.length < L.reason.min) {
    return { ok: false, error: `Give a reason of at least ${L.reason.min} characters; it is recorded in the audit log.` };
  }
  return {
    ok: true,
    input: { maxConcurrentRuns: runs, maxQueriesPerSecond: qps, statementTimeoutMs: ms, reason: reason.slice(0, L.reason.max) },
  };
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '-';
}

function seconds(ms: number): string {
  const s = ms / 1000;
  return `${Number.isInteger(s) ? s : s.toFixed(1)} s`;
}

const INPUT_CLASS = 'rounded border border-border/40 bg-background/40 px-2 py-1.5 text-sm text-foreground';

export function AgentlessCollectionPanel() {
  const [data, setData] = useState<AgentlessSettingsResponse | null>(null);
  const [runs, setRuns] = useState<AgentlessRun[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<AgentlessForm>({
    maxConcurrentRuns: '',
    maxQueriesPerSecond: '',
    statementTimeoutSeconds: '',
    reason: '',
  });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRunsError(null);
    const [s, r] = await Promise.allSettled([
      getAgentlessSettings(),
      listAgentlessRuns({ limit: AGENTLESS_RECENT_RUNS }),
    ]);
    if (s.status === 'fulfilled') {
      setData(s.value);
      setUnavailable(false);
    } else if (s.reason instanceof ApiError && s.reason.status === 404) {
      setUnavailable(true);
    } else {
      setError(problemDetail(s.reason, 'could not load the agentless collection settings'));
    }
    if (r.status === 'fulfilled') {
      setRuns(r.value.runs);
    } else if (!(r.reason instanceof ApiError && r.reason.status === 404)) {
      setRunsError(problemDetail(r.reason, 'could not load the recent runs'));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const startEdit = () => {
    if (!data) return;
    setForm({
      maxConcurrentRuns: String(data.settings.maxConcurrentRuns),
      maxQueriesPerSecond: String(data.settings.maxQueriesPerSecond),
      statementTimeoutSeconds: String(data.settings.statementTimeoutMs / 1000),
      reason: '',
    });
    setFormError(null);
    setNotice(null);
    setEditing(true);
  };

  const save = async () => {
    const parsed = agentlessFormToInput(form);
    if (!parsed.ok) {
      setFormError(parsed.error);
      return;
    }
    setBusy(true);
    setFormError(null);
    setNotice(null);
    try {
      const r = await updateAgentlessSettings(parsed.input);
      setData(r);
      setEditing(false);
      setNotice('Saved. Runs that start from now on use these caps; runs already going keep the caps they started with.');
    } catch (e) {
      setFormError(problemDetail(e, 'the change was refused'));
    } finally {
      setBusy(false);
    }
  };

  const s = data?.settings;
  const set = <K extends keyof AgentlessForm>(k: K, v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <GlassCard className="mt-4 p-0" data-testid="agentless-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <DatabaseZap className="h-4 w-4" />
            Agentless collection
          </CardTitle>
          <CardDescription clamp>
            Collection the controller runs itself against your databases, CMDB and identity provider
            (catalog crawl, value sampling, residual scan, CMDB and identity pulls). These caps bound how
            hard it may lean on those systems. Pause one target from its row in the table, or all of it
            from the rollout controls (agent type: Agentless collection).
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void load()} aria-label="Reload agentless collection">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>

      <div className="space-y-4 px-6 pb-6">
        {loading && !data && !unavailable && !error ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </p>
        ) : null}

        {unavailable ? (
          <p className="text-sm text-muted-foreground" data-testid="agentless-unavailable">
            This controller does not offer agentless collection settings (it predates release 3.1).
          </p>
        ) : null}

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        {s && data ? (
          <section aria-labelledby="agentless-caps-heading" className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 id="agentless-caps-heading" className="text-sm font-semibold text-foreground">
                Caps
              </h3>
              {!editing ? (
                <Button size="sm" variant="ghost" onClick={startEdit}>
                  <Pencil className="mr-1 h-3 w-3" /> Edit caps
                </Button>
              ) : null}
            </div>
            <dl className="grid grid-cols-2 gap-3 text-xs md:grid-cols-4" data-testid="agentless-caps">
              <div className="glass-lite rounded p-3">
                <dt className="mb-0.5 text-muted-foreground">Runs at once</dt>
                <dd className="text-sm tabular-nums text-foreground">{s.maxConcurrentRuns}</dd>
              </div>
              <div className="glass-lite rounded p-3">
                <dt className="mb-0.5 text-muted-foreground">Queries a second</dt>
                <dd className="text-sm tabular-nums text-foreground">{s.maxQueriesPerSecond}</dd>
              </div>
              <div className="glass-lite rounded p-3">
                <dt className="mb-0.5 text-muted-foreground">Statement timeout</dt>
                <dd className="text-sm tabular-nums text-foreground">{seconds(s.statementTimeoutMs)}</dd>
              </div>
              <div className="glass-lite rounded p-3">
                <dt className="mb-0.5 text-muted-foreground">Running now</dt>
                <dd className="text-sm tabular-nums text-foreground" data-testid="agentless-running">
                  {data.running} of {s.maxConcurrentRuns}
                </dd>
              </div>
            </dl>
            <p className="text-xs text-muted-foreground" data-testid="agentless-source">
              {s.isDefault
                ? 'The defaults: nobody has changed them.'
                : `Changed ${when(s.updatedAt)}${s.updatedBy ? ` by ${s.updatedBy}` : ''}${s.reason ? `: ${s.reason}` : ''}`}
            </p>
            <p className="text-xs text-muted-foreground">
              A run over the cap is refused (the caller is told to retry later), not queued.
            </p>

            {editing ? (
              <div className="space-y-2 rounded-md border border-border/40 bg-muted/20 p-3">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label htmlFor="agentless-runs" className="mb-1 block text-xs text-muted-foreground">
                      Runs at once ({AGENTLESS_LIMITS.maxConcurrentRuns.min}-{AGENTLESS_LIMITS.maxConcurrentRuns.max})
                    </label>
                    <input
                      id="agentless-runs"
                      type="number"
                      min={AGENTLESS_LIMITS.maxConcurrentRuns.min}
                      max={AGENTLESS_LIMITS.maxConcurrentRuns.max}
                      step={1}
                      className={`w-28 ${INPUT_CLASS}`}
                      value={form.maxConcurrentRuns}
                      onChange={(e) => set('maxConcurrentRuns', e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="agentless-qps" className="mb-1 block text-xs text-muted-foreground">
                      Queries a second ({AGENTLESS_LIMITS.maxQueriesPerSecond.min}-
                      {AGENTLESS_LIMITS.maxQueriesPerSecond.max.toLocaleString('en-IN')})
                    </label>
                    <input
                      id="agentless-qps"
                      type="number"
                      min={AGENTLESS_LIMITS.maxQueriesPerSecond.min}
                      max={AGENTLESS_LIMITS.maxQueriesPerSecond.max}
                      step="any"
                      className={`w-32 ${INPUT_CLASS}`}
                      value={form.maxQueriesPerSecond}
                      onChange={(e) => set('maxQueriesPerSecond', e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="agentless-timeout" className="mb-1 block text-xs text-muted-foreground">
                      Statement timeout, seconds ({AGENTLESS_LIMITS.statementTimeoutMs.min / 1000}-
                      {AGENTLESS_LIMITS.statementTimeoutMs.max / 1000})
                    </label>
                    <input
                      id="agentless-timeout"
                      type="number"
                      min={AGENTLESS_LIMITS.statementTimeoutMs.min / 1000}
                      max={AGENTLESS_LIMITS.statementTimeoutMs.max / 1000}
                      step="any"
                      className={`w-32 ${INPUT_CLASS}`}
                      value={form.statementTimeoutSeconds}
                      onChange={(e) => set('statementTimeoutSeconds', e.target.value)}
                    />
                  </div>
                  <div className="min-w-48 flex-1">
                    <label htmlFor="agentless-reason" className="mb-1 block text-xs text-muted-foreground">
                      Reason for the change (recorded in the audit log)
                    </label>
                    <input
                      id="agentless-reason"
                      className={`w-full ${INPUT_CLASS}`}
                      value={form.reason}
                      maxLength={AGENTLESS_LIMITS.reason.max}
                      onChange={(e) => set('reason', e.target.value)}
                    />
                  </div>
                </div>
                {formError ? (
                  <p className="text-xs text-destructive" role="alert">
                    {formError}
                  </p>
                ) : null}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    disabled={busy || form.reason.trim().length < AGENTLESS_LIMITS.reason.min}
                    onClick={() => void save()}
                  >
                    {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}
                    Save caps
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
                    <X className="mr-1 h-3 w-3" /> Cancel
                  </Button>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {!unavailable ? (
          <section aria-labelledby="agentless-runs-heading" className="space-y-2">
            <h3 id="agentless-runs-heading" className="text-sm font-semibold text-foreground">
              Recent runs
            </h3>
            {runsError ? (
              <p className="text-sm text-destructive" role="alert">
                {runsError}
              </p>
            ) : runs === null ? null : runs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No agentless collection has run yet.</p>
            ) : (
              <div className="max-h-72 overflow-auto">
                <table className="w-full text-xs" data-testid="agentless-runs">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1 pr-3 font-normal">Target</th>
                      <th className="py-1 pr-3 font-normal">Kind</th>
                      <th className="py-1 pr-3 font-normal">Started</th>
                      <th className="py-1 pr-3 font-normal">Outcome</th>
                      <th className="py-1 pr-3 font-normal">Queries</th>
                      <th className="py-1 pr-3 font-normal">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => {
                      const outcome = agentlessOutcomeLabel(r);
                      return (
                        <tr key={r.id} className="border-t border-border/40 align-top">
                          <td className="py-1 pr-3 font-mono text-[11px] text-foreground break-all">{r.target}</td>
                          <td className="py-1 pr-3 text-foreground whitespace-nowrap">{agentlessKindLabel(r.kind)}</td>
                          <td className="py-1 pr-3 text-muted-foreground whitespace-nowrap">
                            {when(r.startedAt)}
                            {r.startedBy ? <div className="text-[10px]">by {r.startedBy}</div> : null}
                          </td>
                          <td className="py-1 pr-3">
                            <Badge variant={OUTCOME_VARIANT[r.outcome ?? ''] ?? 'glass'} className="text-[10px]">
                              {outcome}
                            </Badge>
                          </td>
                          <td className="py-1 pr-3 tabular-nums text-foreground">
                            {r.queries === null || r.queries === undefined ? '-' : r.queries.toLocaleString('en-IN')}
                          </td>
                          <td className="py-1 pr-3 text-muted-foreground">{r.detail ?? ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ) : null}

        {notice ? <p className="text-sm text-foreground">{notice}</p> : null}
      </div>
    </GlassCard>
  );
}
